import { useCallback, useEffect, useState } from "react";
import { integrationService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Activity, Loader2, TriangleAlert } from "lucide-react";
import { ConnectorIcon, useTenantScope, TenantPicker, SelectTenantNotice, PageHeader, Badge, STATUS_STYLE, RUN_STYLE, fmtDate } from "./shared";

export default function IntegrationMonitoring() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setData(null); return; }
    setLoading(true);
    integrationService.monitoring(params)
      .then(setData)
      .catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [blocked, params]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="integration-monitoring-page">
      <PageHeader icon={Activity} title="Integration Monitoring" subtitle="Health, throughput and recent transactions across all connectors.">
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : loading && !data ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : data ? (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="monitoring-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-muted-foreground border-b border-border">
                  <th className="px-4 py-2">Integration</th><th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-center">Runs</th><th className="px-4 py-2 text-center">Open errors</th>
                  <th className="px-4 py-2">Last run</th><th className="px-4 py-2">Last sync</th>
                </tr>
              </thead>
              <tbody>
                {(data.integrations || []).length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground text-sm">No integrations to monitor.</td></tr>
                ) : data.integrations.map((i) => (
                  <tr key={i.id} className="border-b border-border last:border-0" data-testid={`monitor-row-${i.id}`}>
                    <td className="px-4 py-2"><span className="inline-flex items-center gap-2 text-foreground"><ConnectorIcon code={i.connectorCode} className="h-4 w-4 text-primary" /> {i.name}</span></td>
                    <td className="px-4 py-2"><Badge label={i.status} styleMap={STATUS_STYLE} /></td>
                    <td className="px-4 py-2 text-center text-foreground">{i.runs}</td>
                    <td className="px-4 py-2 text-center">{i.openErrors > 0 ? <span className="inline-flex items-center gap-1 text-red-600 font-semibold"><TriangleAlert className="h-3.5 w-3.5" /> {i.openErrors}</span> : <span className="text-muted-foreground">0</span>}</td>
                    <td className="px-4 py-2">{i.lastRunStatus ? <Badge label={i.lastRunStatus} styleMap={RUN_STYLE} /> : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">{fmtDate(i.lastSyncAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-card border border-border rounded-xl p-5" data-testid="monitoring-transactions">
            <h2 className="text-sm font-bold text-foreground mb-3">Recent transactions</h2>
            {(data.recentTransactions || []).length === 0 ? <p className="text-xs text-muted-foreground">No transactions logged yet.</p> : (
              <div className="space-y-2">
                {data.recentTransactions.map((t) => (
                  <div key={t.id} className="flex items-center justify-between gap-2 border-b border-border pb-2 last:border-0 text-xs" data-testid={`tx-row-${t.id}`}>
                    <span className="text-foreground capitalize">{t.kind} · {t.connectorCode}</span>
                    <span className="text-muted-foreground truncate flex-1 mx-2">{t.detail}</span>
                    <Badge label={t.status === "ok" ? "completed" : "failed"} styleMap={RUN_STYLE} />
                    <span className="text-muted-foreground whitespace-nowrap">{fmtDate(t.createdOn || t.startedAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
